import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoading } from "@/components/common/Loading";
import { StatCard } from "@/components/common/StatCard";
import { RagBadge } from "@/components/common/RagBadge";
import { fmtDate, CLASSIFICATION } from "@/lib/format";

export default function Portfolio() {
  const { setOrgId } = useOrg();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/portfolio").then((r) => setData(r.data)); }, []);

  if (!data) return <PageLoading />;

  const openClient = (id) => { setOrgId(id); navigate("/"); };
  const s = data.summary;

  return (
    <div data-testid="portfolio-page">
      <PageHeader eyebrow="Amden portfolio" title="Consultant Portfolio" subtitle="A live overview of every client, where they stand and where you can help most." icon="LayoutGrid" />

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testId="stat-total" icon="Building2" label="Active clients" value={s.total_clients} tone="dark" />
        <StatCard testId="stat-well" icon="CircleCheck" label="Performing well" value={s.performing_well} tone="green" />
        <StatCard testId="stat-attention" icon="TriangleAlert" label="Attention required" value={s.attention + s.monitor} tone="amber" />
        <StatCard testId="stat-risk" icon="OctagonAlert" label="High risk" value={s.high_risk} tone="red" />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Daily briefing */}
        <div className="amden-card overflow-hidden p-0 lg:col-span-2" data-testid="daily-briefing">
          <div className="flex items-center gap-2.5 bg-[#0B1729] px-6 py-4">
            <Icons.Sparkles size={18} className="text-[#FFC24D]" />
            <h3 className="font-display text-base font-bold text-white">Where you can make the biggest difference today</h3>
          </div>
          <div className="p-6">
            {data.briefing.length ? (
              <div className="space-y-3">
                {data.briefing.map((b, i) => (
                  <button key={i} onClick={() => openClient(b.organisation_id)} className="flex w-full items-start gap-3 rounded-xl border border-[#EEF1F6] p-4 text-left transition-colors hover:border-[#FFC24D] hover:bg-[#F4F6F9]" data-testid={`briefing-${i}`}>
                    <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${CLASSIFICATION[b.classification]?.rag === "red" ? "bg-[#C95555]/10 text-[#C95555]" : "bg-[#D99A32]/12 text-[#B87F1E]"}`}><Icons.AlertCircle size={16} /></span>
                    <div><div className="text-sm text-[#1E2D44]/85">{b.reason}</div></div>
                    <Icons.ChevronRight size={16} className="ml-auto mt-1 shrink-0 text-[#1E2D44]/30" />
                  </button>
                ))}
              </div>
            ) : <p className="text-sm text-[#1E2D44]/50">All clients are in good shape today.</p>}
          </div>
        </div>

        {/* Upcoming sessions */}
        <div className="amden-card p-6">
          <h3 className="mb-4 font-display text-base font-bold">Upcoming sessions</h3>
          {data.sessions.length ? (
            <div className="space-y-3">
              {data.sessions.map((s) => (
                <div key={s.id} className="rounded-lg border border-[#EEF1F6] p-3">
                  <div className="text-sm font-medium text-[#0B1729]">{s.title}</div>
                  <div className="text-xs text-[#1E2D44]/55">{s.company_name} · {fmtDate(s.date)}</div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-[#1E2D44]/50">No sessions scheduled.</p>}
        </div>
      </div>

      {/* Client table */}
      <div className="amden-card mt-6 overflow-hidden p-0">
        <div className="border-b border-[#E4E8EF] px-6 py-4"><h3 className="font-display text-base font-bold">Client portfolio</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E4E8EF] text-left text-xs uppercase tracking-wide text-[#1E2D44]/45">
                <th className="px-6 py-3 font-semibold">Company</th>
                <th className="px-4 py-3 font-semibold">Pack</th>
                <th className="px-4 py-3 font-semibold">Health</th>
                <th className="px-4 py-3 font-semibold">Plan</th>
                <th className="px-4 py-3 font-semibold">Dossier</th>
                <th className="px-4 py-3 font-semibold">Actions</th>
                <th className="px-4 py-3 font-semibold">Overdue</th>
                <th className="px-4 py-3 font-semibold">Check-in</th>
                <th className="px-4 py-3 font-semibold">KPIs</th>
                <th className="px-4 py-3 font-semibold">Overall</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.clients.map((c) => (
                <tr key={c.organisation_id} onClick={() => openClient(c.organisation_id)} className="cursor-pointer border-b border-[#EEF1F6] transition-colors hover:bg-[#F4F6F9]" data-testid={`client-row-${c.organisation_id}`}>
                  <td className="px-6 py-3.5">
                    <div className="font-semibold text-[#0B1729]">{c.company_name}</div>
                    <div className="text-xs text-[#1E2D44]/50">{c.consultant_name}</div>
                  </td>
                  <td className="px-4 py-3.5" data-testid={`client-pack-${c.organisation_id}`}>
                    {c.onboarding_pack
                      ? <span className="inline-flex items-center gap-1 rounded-full bg-[#0B1729]/5 px-2 py-0.5 text-xs font-medium text-[#0B1729]"><Icons.PackageOpen size={11} /> {c.onboarding_pack}</span>
                      : <span className="text-xs text-[#1E2D44]/35">—</span>}
                  </td>
                  <td className="px-4 py-3.5 font-medium">{c.health_score ?? "—"}</td>
                  <td className="px-4 py-3.5">{c.plan_progress != null ? `${c.plan_progress}%` : "—"}</td>
                  <td className="px-4 py-3.5" data-testid={`dossier-progress-${c.organisation_id}`}>
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-14 overflow-hidden rounded-full bg-[#E4E8EF]"><div className="h-full rounded-full bg-[#FFB000]" style={{ width: `${c.dossier_progress || 0}%` }} /></div>
                      <span className="text-xs text-[#1E2D44]/55">{c.dossier_progress || 0}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3.5">{c.completion_rate}%</td>
                  <td className="px-4 py-3.5"><span className={c.overdue_actions ? "font-semibold text-[#C95555]" : "text-[#1E2D44]/50"}>{c.overdue_actions}</span></td>
                  <td className="px-4 py-3.5">{c.checkin_status === "done" ? <RagBadge rag="green">Done</RagBadge> : <RagBadge rag="amber">Missing</RagBadge>}</td>
                  <td className="px-4 py-3.5"><span className={c.red_kpis ? "font-semibold text-[#C95555]" : "text-[#1E2D44]/50"}>{c.red_kpis} off</span></td>
                  <td className="px-4 py-3.5"><RagBadge rag={CLASSIFICATION[c.classification]?.rag}>{CLASSIFICATION[c.classification]?.label}</RagBadge></td>
                  <td className="px-4 py-3.5 text-right"><Icons.ChevronRight size={16} className="text-[#1E2D44]/30" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
